import type { RefCallback } from 'react';
import { Inbox } from 'lucide-react';
import { motion } from 'motion/react';

import { DASHBOARD_ROUTINE_TAB_ANCHOR, DASHBOARD_URGENT_TAB_ANCHOR } from '@/app/hints';
import {
  type PatientNavigatorItemData,
  type ScopedPatientTab,
  useScopedPatientTabs,
} from '@/features/patient-navigator';
import { cn } from '@/lib/utils';
import { EmptyState, useHintAnchor } from '@/shared/ui/primitives';

import { PatientQueueCard } from './PatientQueueCard';

export type QueueFilterTab = ScopedPatientTab;

function pickTabAnchorRef(
  tabId: ScopedPatientTab,
  urgentRef: RefCallback<HTMLElement>,
  routineRef: RefCallback<HTMLElement>
): RefCallback<HTMLElement> | undefined {
  switch (tabId) {
    case 'urgent':
      return urgentRef;
    case 'routine':
      return routineRef;
    case 'all':
      return undefined;
    default: {
      const exhaustive: never = tabId;
      void exhaustive;
      return undefined;
    }
  }
}

interface PatientQueueProps {
  patients: PatientNavigatorItemData[];
  isPatientsLoading: boolean;
  activeTab: QueueFilterTab;
  onTabChange: (tab: QueueFilterTab) => void;
  onSelectPatient: (userId: string) => void;
}

/**
 * PatientQueue — filterable, sorted patient triage queue.
 */
export function PatientQueue({
  patients,
  isPatientsLoading,
  activeTab,
  onTabChange,
  onSelectPatient,
}: PatientQueueProps) {
  const { availableTabs, filtered } = useScopedPatientTabs({
    patients,
    activeTab,
    isPatientsLoading,
    onTabChange,
  });
  const urgentTabAnchorRef = useHintAnchor(DASHBOARD_URGENT_TAB_ANCHOR);
  const routineTabAnchorRef = useHintAnchor(DASHBOARD_ROUTINE_TAB_ANCHOR);

  return (
    <motion.div
      className={cn('flex flex-col')}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, delay: 0.05 }}
    >
      {/* Visually-hidden landmark — the dashboard header subtitle states the
          same intent for sighted users; this preserves the heading outline
          for screen readers and serves as the tablist's accessible name. */}
      <h2 id="patient-queue-heading" className="sr-only">
        Patient queue
      </h2>

      {/* Filter Tabs */}
      <div className="flex gap-1" role="tablist" aria-labelledby="patient-queue-heading">
        {availableTabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const anchorRef = pickTabAnchorRef(tab.id, urgentTabAnchorRef, routineTabAnchorRef);

          return (
            <button
              key={tab.id}
              ref={anchorRef}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`queue-panel-${tab.id}`}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-white/50 hover:bg-white/8 hover:text-white/70'
              )}
            >
              {tab.label}
              {tab.id !== 'all' ? (
                <span
                  className={cn(
                    'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] tabular-nums leading-none',
                    isActive ? 'bg-white/15 text-white' : 'bg-white/8 text-white/40'
                  )}
                >
                  {tab.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Patient List */}
      <div id={`queue-panel-${activeTab}`} role="tabpanel" aria-label={`${activeTab} patients`}>
        {isPatientsLoading ? (
          <div className="mt-3 space-y-3" role="status" aria-label="Loading patients">
            <div className="h-3 w-2/5 animate-pulse rounded-full bg-gradient-to-r from-muted/50 via-muted/80 to-muted/50" />
            <div className="h-3 w-3/5 animate-pulse rounded-full bg-gradient-to-r from-muted/40 via-muted/70 to-muted/40 [animation-delay:150ms]" />
            <div className="h-3 w-1/3 animate-pulse rounded-full bg-gradient-to-r from-muted/30 via-muted/60 to-muted/30 [animation-delay:300ms]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16">
            <EmptyState
              icon={<Inbox className="h-10 w-10" />}
              title="No patients"
              description={`No ${activeTab} patients right now.`}
              className="text-white/60 [&_h3]:text-white/80"
            />
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            {filtered.map((patient, index) => (
              <PatientQueueCard
                key={patient.userId}
                patient={patient}
                index={index}
                onSelect={() => onSelectPatient(patient.userId)}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
